/**
 * @Relay no-runtime
 */
/**
 * @graphql agg
 */

import { useContext, useEffect, useMemo } from 'react';
import graphql from 'graphql-tag';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import {
	createProductSpecificAnalyticsIntegrationPayload,
	ErrorType,
	Severity,
	Product,
	Team,
} from '@atlassian/growth-commons-error-analytics';

import { SPAViewContext } from '@confluence/spa-view-context/entry-points/SPAViewContext';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { getAGGClient } from '@confluence/graphql/entry-points/getAGGClient';

import { ConfluenceEdition } from '../../Upgrade/__types__/waitForEditionChangeQuery';

import type {
	useIsEligibleForUpflowV2ConfluenceStandardFGsExperimentQuery as useIsEligibleForUpflowV2ConfluenceStandardFGsExperimentQuery$data,
	useIsEligibleForUpflowV2ConfluenceStandardFGsExperimentQueryVariables as useIsEligibleForUpflowV2ConfluenceStandardFGsExperimentQuery$variables,
} from './__types__/useIsEligibleForUpflowV2ConfluenceStandardFGsExperimentQuery';

const extractBillingAdminStatus = (
	billingData: useIsEligibleForUpflowV2ConfluenceStandardFGsExperimentQuery$data | undefined,
): boolean | null | undefined => {
	return billingData?.tenantContexts?.[0]?.entitlementInfo?.entitlement?.transactionAccount
		?.isCurrentUserBillingAdmin;
};

export const useIsEligibleForUpflowV2ConfluenceStandardFGsExperiment = ({
	skip = false,
	currentEdition,
	targetEdition,
}: {
	skip: boolean;
	currentEdition: ConfluenceEdition | null;
	targetEdition: ConfluenceEdition | null;
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { cloudId } = useSessionData();
	const { isSiteAdmin } = useContext(SPAViewContext);
	const isBasicEligible = Boolean(
		cloudId &&
			currentEdition === ConfluenceEdition.FREE &&
			targetEdition === ConfluenceEdition.STANDARD &&
			isSiteAdmin,
	);

	const {
		data: billingAdminData,
		loading: isBillingAdminQueryLoading,
		error: billingAdminQueryError,
	} = useQuery<
		useIsEligibleForUpflowV2ConfluenceStandardFGsExperimentQuery$data,
		useIsEligibleForUpflowV2ConfluenceStandardFGsExperimentQuery$variables
	>(
		graphql`
			query useIsEligibleForUpflowV2ConfluenceStandardFGsExperimentQuery($cloudId: ID!) {
				tenantContexts(cloudIds: [$cloudId]) {
					entitlementInfo(hamsProductKey: "confluence.ondemand") {
						entitlement {
							id
							transactionAccount {
								... on Node {
									id
									__typename
								}
								isCurrentUserBillingAdmin
							}
						}
					}
				}
			}
		`,
		{
			client: getAGGClient(),
			variables: { cloudId },
			skip: skip || !isBasicEligible,
		},
	);

	useEffect(() => {
		if (billingAdminQueryError) {
			const eventPayload = createProductSpecificAnalyticsIntegrationPayload({
				product: Product.CONFLUENCE,
				team: Team.GROWTH_LUNA,
				experimentMeta: {
					experimentKey: 'grw-1930_confluence_upv2_standard_ab',
				},
				errorType: ErrorType.GRAPHQL,
				errorMessage: billingAdminQueryError.message,
				source: 'useIsEligibleForUpflowV2ConfluenceStandardFGsExperiment hook',
				severity: Severity.HIGH,
			});

			createAnalyticsEvent(eventPayload).fire();
		}
	}, [billingAdminQueryError, createAnalyticsEvent]);

	const isBillingAdmin = useMemo(() => {
		return extractBillingAdminStatus(billingAdminData);
	}, [billingAdminData]);

	return {
		isEligible: Boolean(isBasicEligible && isBillingAdmin),
		isSiteAdmin,
		isBillingAdmin,
		loading: isBillingAdminQueryLoading,
		error: billingAdminQueryError,
	};
};
